export type { NWTEvent } from './nw-tracking-types';

export type { Events, TrackingEvent } from './types';

import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import snakeCaseKeys from 'snakecase-keys';

import { Eventual } from '@xing-com/crate-xinglet';
import type { ViewerData } from '@xing-com/hub';
import { useExperimentTrackingData, useViewerData } from '@xing-com/hub';
import ttt from '@xing-com/ticktricktrack';

import { SCHEMA_VERSION as NWT_SCHEMA_VERSION } from './nw-tracking-types';
import type { UseTrackingReturn, TrackingEvent } from './types';

export const SCHEMA_VERSION = '1.3.0';

const MOBILE_BREAKPOINT = 739;
const isMobile = (): boolean =>
  typeof globalThis.window !== 'undefined' &&
  globalThis.window.innerWidth <= MOBILE_BREAKPOINT;

function handleTrackingEvent(
  payload: TrackingEvent,
  {
    locale,
    userId,
    PropExperiment,
    PropMemberships,
  }: {
    locale: string;
    userId?: string;
    PropExperiment?: string;
    PropMemberships?: string;
  }
): void {
  const { type, ...event } = payload;

  event.channel = isMobile() ? 'webm' : 'web';
  event.sdkName = 'crate';
  event.applicationLanguage = locale;

  if (PropExperiment) {
    event.experiment = PropExperiment;
  }

  const subscriptions = [PropMemberships, event.subscriptions]
    .filter(Boolean)
    .join(',');

  if (subscriptions) {
    event.subscriptions = subscriptions;
  }

  if (!event.userId && userId) {
    // TODO: check why the UserId can be a string or a number
    event.userId = `${userId}`;
  }

  // Overwrite the schema version with the current one defined by the nwt tracking lib
  event.schemaVersion = NWT_SCHEMA_VERSION;

  ttt.nwtEvent(snakeCaseKeys(event));
}

let resolve: ((value: ViewerData) => void) | undefined;
let promise: Eventual<ViewerData> | undefined;

if (typeof window !== 'undefined') {
  promise = new Eventual<ViewerData>((res) => {
    resolve = res;
  });
}

export const useViewerDataEventual = (): Eventual<ViewerData> | undefined => {
  const { data: viewerData } = useViewerData();

  if (resolve && viewerData) {
    resolve(viewerData);
    resolve = undefined;
  }

  return promise;
};

export const useTracking = (): UseTrackingReturn => {
  const { locale } = useIntl();
  const { PropExperiment } = useExperimentTrackingData();
  const userDataEventual = useViewerDataEventual();

  const track = useCallback(
    (payload: TrackingEvent): void => {
      userDataEventual?.then((viewerData) => {
        const { user, propMemberships } = viewerData;

        handleTrackingEvent(payload, {
          locale,
          PropExperiment,
          PropMemberships: propMemberships,
          userId: user?.id,
        });
      });
    },
    [userDataEventual, locale, PropExperiment]
  );

  return { track };
};
